import { useEffect, useRef, useState, useSyncExternalStore } from 'react'

/**
 * Scroll-reveal hook — frontend-spec §3d.
 *
 * Returns a `ref` for the sentinel element and an `isVisible` boolean that flips
 * to true (once) when the element scrolls into view. Reduced-motion users resolve
 * to visible immediately.
 *
 * SSR-safe + lint-clean: `prefers-reduced-motion` is read via `useSyncExternalStore`
 * (server snapshot = motion allowed, so no hydration mismatch), and the only
 * `setState` happens inside the async IntersectionObserver callback — never
 * synchronously in the effect body (which trips react-hooks "setState in effect").
 *
 * Entrance vocabulary (§E): opacity 0→1 + translateY(12px→0), 280ms ease-out,
 * 60ms sibling stagger via `delay` prop on ScrollRevealSection.
 */
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

function subscribeReducedMotion(onChange: () => void): () => void {
  const mq = window.matchMedia(REDUCED_MOTION_QUERY)
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}

export function useScrollReveal<T extends Element = HTMLDivElement>() {
  const ref = useRef<T>(null)
  const prefersReduced = useSyncExternalStore(
    subscribeReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => false
  )
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    // Reduced motion → already visible via `prefersReduced`; skip the observer entirely.
    if (prefersReduced || revealed) return

    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true) // async callback — not a synchronous setState-in-effect
          observer.disconnect()
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [prefersReduced, revealed])

  return { ref, isVisible: prefersReduced || revealed }
}
